import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { getShifts, getShiftEnums } from '../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    WEEKDAYS,
    toBool,
    todayISO,
    formatDateOnly,
    shiftTimeRange,
    sameDays,
} from '../../../consts/shiftConstants';

// The shift block on the employee form — the primary way a shift gets assigned.
//
// Renders nothing at all without `shift.assign`: sending shift_id without it is
// a flat 403, so an admin who cannot assign should not see the controls.

const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1';

const EmployeeShiftSection = ({ values, original, errors = {}, onChange, disabled }) => {
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const canAssign = can(PERMISSIONS.SHIFT_ASSIGN);

    const { shifts, shiftsLoading, shiftEnums } = useSelector(state => state.ShiftReducer || {});

    useEffect(() => {
        if (!canAssign) return;
        dispatch(getShiftEnums());
        // Only active shifts are assignable — an inactive one is a 409.
        dispatch(getShifts({ is_active: true, limit: 100 }));
    }, [dispatch, canAssign]);

    const shiftOptions = useMemo(() => (
        (shifts || [])
            .filter(s => toBool(s.is_active) || String(s.shift_id) === String(original?.shift_id))
            .map(s => ({
                value: String(s.shift_id),
                label: `${s.shift_code} · ${s.shift_name} (${shiftTimeRange(s)})`,
            }))
    ), [shifts, original]);

    const weekdays = shiftEnums?.weekdays?.length
        ? shiftEnums.weekdays.map(w => ({
            ...w,
            short: WEEKDAYS.find(d => d.value === w.value)?.short || w.name?.slice(0, 3),
        }))
        : WEEKDAYS;

    if (!canAssign) return null;

    const selectedShift = (shifts || []).find(s => String(s.shift_id) === String(values.shift_id));
    const originalShiftId = original?.shift_id ? String(original.shift_id) : '';
    // The "effective from" box only appears once the shift actually changes:
    // most changes are "from now", and the API defaults to today anyway.
    const shiftChanged = original != null && String(values.shift_id || '') !== originalShiftId;
    const offsChanged = original != null && !sameDays(values.weekly_off_days, original.weekly_off_days);
    const clearing = shiftChanged && !values.shift_id;

    const setField = (name, value) => onChange({ target: { name, value } });

    const toggleOffDay = (day) => {
        const current = values.weekly_off_days || [];
        const next = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day].sort((a, b) => a - b);
        setField('weekly_off_days', next);
    };

    return (
        <div className="tw-p-5 tw-border-b tw-border-gray-100">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                <Icon icon="hugeicons:time-schedule" className="tw-text-rose-600" />
                <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Shift</h2>
                {/* Makes the date-ranged model visible without explaining it */}
                {original?.shift_id && original.effective_from && (
                    <span className="tw-text-[11px] tw-text-gray-400">
                        since {formatDateOnly(original.effective_from)}
                    </span>
                )}
            </div>

            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                <div>
                    <label className={labelClass}>Assigned Shift</label>
                    <SearchableDropdown
                        small
                        value={values.shift_id}
                        onChange={(v) => setField('shift_id', v === 'all' ? '' : v)}
                        options={shiftOptions}
                        loading={shiftsLoading}
                        disabled={disabled}
                        placeholder="No shift"
                    />
                    {errors.shift_id
                        ? <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.shift_id}</p>
                        : selectedShift && (
                            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                                {shiftTimeRange(selectedShift)} · {selectedShift.scheduled_display || ''} working
                            </p>
                        )}
                </div>

                {shiftChanged && (
                    <div>
                        <label className={labelClass}>
                            {clearing ? 'Shift ends after' : 'New shift starts on'}
                        </label>
                        <input
                            type="date"
                            name="shift_effective_from"
                            value={values.shift_effective_from || ''}
                            onChange={(e) => setField('shift_effective_from', e.target.value)}
                            disabled={disabled}
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                            {clearing
                                ? `Leave blank for today (${formatDateOnly(todayISO())}). The assignment closes the day before.`
                                : `Leave blank for today (${formatDateOnly(todayISO())}). The previous assignment closes the day before, so each period keeps the shift it was worked on.`}
                        </p>
                    </div>
                )}

                <div className={shiftChanged ? '' : 'sm:tw-col-span-1 lg:tw-col-span-2'}>
                    <label className={labelClass}>Weekly Offs</label>
                    <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                        {weekdays.map(d => {
                            const selected = (values.weekly_off_days || []).includes(d.value);
                            return (
                                <button
                                    key={d.value}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => toggleOffDay(d.value)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-border disabled:tw-opacity-50 ${selected
                                        ? 'tw-bg-gray-700 tw-text-white tw-border-gray-700'
                                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'}`}
                                >
                                    {d.short}
                                </button>
                            );
                        })}
                    </div>
                    {errors.weekly_off_days
                        ? <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.weekly_off_days}</p>
                        : <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">Days this employee does not work.</p>}
                </div>
            </div>

            {clearing && (
                <p className="tw-text-[11px] tw-text-amber-700 tw-bg-amber-50 tw-rounded-md tw-px-3 tw-py-2 tw-mt-3 tw-mb-0 tw-flex tw-items-start tw-gap-1.5">
                    <Icon icon="mdi:alert-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                    Saving with no shift selected will remove this employee&apos;s shift.
                </p>
            )}

            {!clearing && (shiftChanged || offsChanged) && original?.shift_id && (
                <p className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-rounded-md tw-px-3 tw-py-2 tw-mt-3 tw-mb-0 tw-flex tw-items-start tw-gap-1.5">
                    <Icon icon="mdi:information-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                    {shiftChanged
                        ? 'The current assignment will be closed and a new one opened, so past periods keep the shift they were actually worked on.'
                        : 'The weekly offs on the current assignment will be updated.'}
                </p>
            )}
        </div>
    );
};

export default EmployeeShiftSection;
